import React from 'react';
import MarketingLayout from '../components/MarketingLayout';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Button,
  Chip,
  Paper,
} from '@mui/material';
import { howItWorks } from '../data/howItWorksContent';

const HowItWorks = () => {
  const { hero, steps, paths, timeline, support } = howItWorks;

  return (
    <MarketingLayout>
    <Box sx={{ backgroundColor: '#f8f9fa' }}>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #052d50 0%, #478289 100%)',
          color: 'white',
          py: 8,
          textAlign: 'center',
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="h2" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
            {hero.title}
          </Typography>
          <Typography variant="h5" gutterBottom sx={{ mb: 2, opacity: 0.9 }}>
            {hero.subtitle}
          </Typography>
          <Typography variant="body1" sx={{ fontSize: '1.1rem', opacity: 0.9 }}>
            {hero.description}
          </Typography>
        </Container>
      </Box>

      {/* Steps Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography variant="h3" gutterBottom sx={{ fontWeight: 600, textAlign: 'center', mb: 6 }}>
          5 Simple Steps to Start Investing
        </Typography>

        <Stepper orientation="vertical" sx={{ backgroundColor: 'transparent' }}>
          {steps.map((step, index) => (
            <Step key={index} active={true} completed={false}>
              <StepLabel
                StepIconProps={{
                  sx: {
                    fontSize: '2rem',
                    color: '#478289',
                    '& .MuiStepIcon-text': { fill: 'white', fontWeight: 600 },
                  },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography variant="h5" sx={{ fontWeight: 600 }}>
                    {step.title}
                  </Typography>
                  <Chip label={step.duration} size="small" sx={{ backgroundColor: '#e3f2fd' }} />
                </Box>
              </StepLabel>
              <StepContent>
                <Paper elevation={2} sx={{ p: 3, mb: 3, backgroundColor: 'white' }}>
                  <Box sx={{ fontSize: 48, mb: 2 }}>{step.icon}</Box>
                  <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
                    {step.description}
                  </Typography>
                  <Box component="ul" sx={{ pl: 2 }}>
                    {step.details.map((detail, idx) => (
                      <Typography component="li" variant="body2" key={idx} sx={{ mb: 1 }}>
                        {detail}
                      </Typography>
                    ))}
                  </Box>
                </Paper>
              </StepContent>
            </Step>
          ))}
        </Stepper>
      </Container>

      {/* Investment Paths */}
      <Box sx={{ backgroundColor: 'white', py: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="h3" gutterBottom sx={{ fontWeight: 600, textAlign: 'center', mb: 2 }}>
            Choose Your Investment Path
          </Typography>
          <Typography variant="body1" sx={{ textAlign: 'center', mb: 6, color: 'text.secondary' }}>
            Select the approach that best fits your investment style and goals
          </Typography>

          <Grid container spacing={4}>
            {paths.map((path, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    border: '2px solid #e0e0e0',
                    '&:hover': { borderColor: '#478289', transform: 'translateY(-4px)', transition: 'all 0.3s' },
                  }}
                >
                  <CardContent sx={{ flexGrow: 1, p: 4 }}>
                    <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: '#052d50' }}>
                      {path.name}
                    </Typography>
                    <Typography variant="subtitle2" color="primary" gutterBottom sx={{ mb: 2 }}>
                      {path.bestFor}
                    </Typography>
                    <Typography variant="h6" sx={{ mb: 3, color: '#478289', fontWeight: 700 }}>
                      Min: {path.minInvestment}
                    </Typography>
                    <Box component="ul" sx={{ pl: 2 }}>
                      {path.features.map((feature, idx) => (
                        <Typography component="li" variant="body2" key={idx} sx={{ mb: 1.5 }}>
                          {feature}
                        </Typography>
                      ))}
                    </Box>
                  </CardContent>
                  <Box sx={{ p: 3, pt: 0 }}>
                    <Button
                      variant="contained"
                      fullWidth
                      sx={{
                        backgroundColor: '#478289',
                        '&:hover': { backgroundColor: '#3a6f71' },
                        py: 1.5,
                        fontWeight: 600,
                      }}
                    >
                      {path.cta}
                    </Button>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Timeline */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography variant="h3" gutterBottom sx={{ fontWeight: 600, textAlign: 'center', mb: 6 }}>
          {timeline.title}
        </Typography>

        <Grid container spacing={4}>
          {timeline.phases.map((phase, index) => (
            <Grid item xs={12} md={4} key={index}>
              <Card sx={{ height: '100%', backgroundColor: '#f5f5f5' }}>
                <CardContent sx={{ p: 4 }}>
                  <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: '#478289' }}>
                    {phase.phase}
                  </Typography>
                  <Box component="ul" sx={{ pl: 2, mt: 2 }}>
                    {phase.activities.map((activity, idx) => (
                      <Typography component="li" variant="body2" key={idx} sx={{ mb: 1.5 }}>
                        {activity}
                      </Typography>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Support Services */}
      <Box sx={{ backgroundColor: 'white', py: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="h3" gutterBottom sx={{ fontWeight: 600, textAlign: 'center', mb: 2 }}>
            {support.title}
          </Typography>
          <Typography variant="body1" sx={{ textAlign: 'center', mb: 6, color: 'text.secondary' }}>
            {support.description}
          </Typography>

          <Grid container spacing={4}>
            {support.services.map((service, index) => (
              <Grid item xs={12} sm={6} key={index}>
                <Box
                  sx={{
                    p: 3,
                    backgroundColor: '#f8f9fa',
                    borderRadius: 2,
                    height: '100%',
                    '&:hover': { backgroundColor: '#e3f2fd' },
                  }}
                >
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#052d50' }}>
                    {service.name}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {service.description}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #052d50 0%, #478289 100%)',
          color: 'white',
          py: 8,
          textAlign: 'center',
        }}
      >
        <Container maxWidth="md">
          <Typography variant="h3" gutterBottom sx={{ fontWeight: 600 }}>
            Ready to Get Started?
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, fontSize: '1.1rem', opacity: 0.9 }}>
            Join thousands of investors accessing the best Israeli tech startups
          </Typography>
          <Button
            variant="contained"
            size="large"
            sx={{
              backgroundColor: 'white',
              color: '#052d50',
              px: 6,
              py: 2,
              fontSize: '1.1rem',
              fontWeight: 600,
              '&:hover': { backgroundColor: '#f5f5f5' },
            }}
          >
            Create Your Account
          </Button>
        </Container>
      </Box>
    </Box>
  
    </MarketingLayout>
  );
};

export default HowItWorks;
